import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import Menu from '@material-ui/icons/Menu';
import AddIcon from '@material-ui/icons/Add';
import UserMenu from '../../../organisms/UserMenu';
import { Hamburger } from '../../../organisms/InspectorSidebar/InspectorSidebar';
import ClientSidebar from './ClientSidebar';
import {
  setShowDrawerContent,
  setShowNewProjectPopup,
} from '../../../redux/slices/generalSlice';
import { RootState } from '../../../redux/root';
import { ModalDefaultDraggable } from '../../../molecules';
import MarketingMessage from './Common/MarketingMessage';
import NewProject from './pages/Dashboard/NewProject/NewProject';
import ProjectCreatedModal from './pages/Dashboard/NewProject/ProjectCreatedModal';

type Props = {
  topCaption?: string;
  bottomCaption?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  showNewProject?: boolean;
};
const ClientContainer: FC<Props> = ({
  headerTitle,
  headerSubtitle,
  showNewProject = true,
  children,
}) => {
  const dispatch = useDispatch();
  const info = useSelector(
    (s: RootState) => s.userAuth.info,
  );
  const showNewProjectPopup = useSelector(
    (s: RootState) => s.general.showNewProjectPopup,
  );
  const [showCreatedModal, setShowCreatedModal] = useState<boolean>(false);
  const firstName = info?.firstName || 'there';

  useEffect(() => {
    const html = document.documentElement;
    const app = document.getElementById('app');
    const prev = {
      htmlHeight: html.style.height,
      htmlOverflow: html.style.overflow,
      bodyHeight: document.body.style.height,
      bodyOverflow: document.body.style.overflow,
      appHeight: app?.style.height,
      appOverflow: app?.style.overflow,
    };
    html.style.height = '100%';
    html.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
    if (app) {
      app.style.height = '100%';
      app.style.overflow = 'hidden';
    }
    return () => {
      html.style.height = prev.htmlHeight;
      html.style.overflow = prev.htmlOverflow;
      document.body.style.height = prev.bodyHeight || '100%';
      document.body.style.overflow = prev.bodyOverflow;
      if (app) {
        app.style.height = prev.appHeight || '100%';
        app.style.overflow = prev.appOverflow;
      }
    };
  }, []);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const openSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const title = headerTitle || `Welcome back, ${firstName}!`;
  const subtitle =
    headerSubtitle ||
    "Here's what's happening with your projects today.";

  return (
    <Wrapper>
      <Layout>
        <LayoutWithNav>
          <Hamburger>
            <Menu onClick={() => openSidebar()} />
          </Hamburger>
          <SidebarDesktopContainer>
            <ClientSidebar />
          </SidebarDesktopContainer>
          {showSidebar && (
            <>
              <SidebarBackdrop onClick={() => setShowSidebar(false)} />
              <SidebarMobileContainer onClick={() => setShowSidebar(false)}>
                <ClientSidebar isMobile />
              </SidebarMobileContainer>
            </>
          )}
          <MainArea>
            <PortalHeader>
              <HeaderSlot>
                <HeaderGreeting>
                  <HeaderTitle>{title}</HeaderTitle>
                  <HeaderSubtitle>{subtitle}</HeaderSubtitle>
                </HeaderGreeting>
                <MarketingMessage />
              </HeaderSlot>
              <UserArea>
                {/* Sits with the avatar, not in HeaderSlot. In the left slot it
                    only landed next to "CC" when the announcement happened to
                    fill the row - with no announcement, or a short one, it
                    floated mid-header. */}
                {showNewProject && (
                  <NewProjectButton
                    type="button"
                    aria-label="New Project"
                    onClick={() =>
                      dispatch(setShowNewProjectPopup(true))
                    }
                  >
                    <AddIcon />
                    <NewProjectLabel>New Project</NewProjectLabel>
                  </NewProjectButton>
                )}
                <UserMenu />
              </UserArea>
            </PortalHeader>
            <BodyArea>{children}</BodyArea>
          </MainArea>
        </LayoutWithNav>
        <Footer />
      </Layout>
      {showNewProjectPopup && (
        <ModalDefaultDraggable
          title="New Project"
          onClose={() => dispatch(setShowNewProjectPopup(false))}
          addContactModal
          maxHeight="700px"
        >
          <NewProject setShowModal={setShowCreatedModal} />
        </ModalDefaultDraggable>
      )}
      <ProjectCreatedModal
        showModal={showCreatedModal}
        setShowModal={setShowCreatedModal}
      />
    </Wrapper>
  );
};

export default ClientContainer;

const Layout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  width: 100%;
  flex-shrink: 0;
`;

const SidebarDesktopContainer = styled.div`
  flex-shrink: 0;
  height: 100%;
`;

const SidebarMobileContainer = styled.div`
  padding-top: 84px;
  position: absolute;
  z-index: 21;
`;

const SidebarBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 20;

  @media only screen and (min-width: 841px) {
    display: none;
  }
`;

const MainArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
`;

const PortalHeader = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 12px 24px;
  background: var(--neutral-0);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media only screen and (max-width: 840px) {
    padding: 0 16px;
    padding-left: 64px;
  }
`;

const HeaderSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;
`;

const HeaderGreeting = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Takes only the width it needs so the announcement banner can grow into the
     rest of the row - but not less than this, or the title ellipsizes to
     "Welcome back, C...". Released below 840px, where the subtitle is hidden
     and the banner has already collapsed to its icon. */
  flex: 0 1 auto;
  min-width: 260px;

  @media only screen and (max-width: 840px) {
    min-width: 0;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--mainText);
  letter-spacing: -0.01em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderSubtitle = styled.div`
  margin-top: 2px;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media only screen and (max-width: 540px) {
    display: none;
  }
`;

const NewProjectButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    font-size: 18px;
  }

  @media only screen and (max-width: 540px) {
    width: 40px;
    padding: 0;
    justify-content: center;
    gap: 0;
  }
`;

const NewProjectLabel = styled.span`
  @media only screen and (max-width: 540px) {
    display: none;
  }
`;

const UserArea = styled.div`
  display: flex;
  align-items: center;
  /* Matches PortalHeader's own gap so the button reads as part of the same
     right-hand cluster as the avatar. */
  gap: 16px;
  margin-left: auto;
  flex-shrink: 0;

  @media only screen and (max-width: 540px) {
    gap: 10px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
  max-width: 100vw;
`;

const LayoutWithNav = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
`;

const BodyArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
